import { colors } from "../styles/colors";
import { Link } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";

export default function PaymentTermsClause() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <header className="px-6 md:px-12 lg:px-24 py-6 " style={{ borderColor: colors.border }}>
        <Link to="/" className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          <img src="/clauseclear-logo.png" alt="ClauseClear" className="h-10 object-contain" />

        </Link>
      </header>

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8" style={{ color: colors.text }}>
            Plain-English Explanation of Payment Terms Clause
          </h1>

          <p className="text-base md:text-lg leading-relaxed mb-12" style={{ color: colors.textMuted }}>
            A payment terms clause specifies when, how, and how much you'll be paid for your work or services.
          </p>

          <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Plain-English Explanation</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              This clause outlines the payment schedule, amounts, methods, and any conditions that must be met before payment is released. It may include milestones, invoicing procedures, late payment penalties, and accepted payment methods.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Why This Clause Exists</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Clear payment terms prevent disputes about money, create predictable cash flow for both parties, and establish consequences for late payment. They ensure everyone understands financial obligations before work begins, reducing misunderstandings about amounts, timing, and conditions.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Typical Effects</h2>
            <ul className="space-y-3 ml-6">
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Defines when invoices can be issued and payment is due
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                May tie payments to milestones or deliverable completion
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Establishes interest charges or penalties for late payment
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Can allow work suspension if payments are overdue
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>What to Watch Out For</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Check payment schedules align with your cash flow needs, whether deposits or upfront payments are required, how expenses are handled, and what happens if deliverables are disputed. Net 60 or Net 90 terms can create significant cash flow problems for small businesses.
            </p>
          </section>

          <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Important Context</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Some jurisdictions have laws protecting small businesses from unreasonable payment terms. Verbal payment agreements may be hard to enforce. This explanation provides general information but doesn't constitute legal advice about your specific situation.
            </p>
          </section>

          <section className="mb-12">
            <div className="flex gap-4 mb-8">
              <Link to="/clauses" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
                ← All Clauses
              </Link>
              <Link to="/clauses/business" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
                Business Clauses
              </Link>
            </div>
            <h2 className="text-2xl font-medium mb-6" style={{ color: colors.accent }}>Related Clauses</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to="/explain-scope-of-work-clause" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Scope of Work</h3>
                <p className="text-sm" style={{ color: colors.textMuted }}>Understand project boundaries</p>
              </Link>
              <Link to="/explain-termination-for-convenience-clause" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Termination for Convenience</h3>
                <p className="text-sm" style={{ color: colors.textMuted }}>Learn about early contract exits</p>
              </Link>
            </div>
          </section>

          <div className="text-center p-8 rounded-2xl" style={{ borderColor: colors.border }}>
            <p className="text-lg mb-4" style={{ color: colors.textMuted }}>Want to understand your own clause?</p>
            <Link to="/" className="inline-flex items-center gap-2 text-white hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all" style={{ backgroundColor: '#2F3E30' }}>
              Try ClauseClear
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Sponsorship Placeholder */}
          <SponsorPlaceholder variant="full" />
        </div>
      </article>

      <footer className="px-6 md:px-12 lg:px-24 py-12 text-center " style={{ borderColor: colors.border }}>
        <p className="text-sm mb-2" style={{ color: colors.textMuted }}>ClauseClear is an educational tool. It does not provide legal advice.</p>
        <a href="https://buymeacoffee.com/jamesmoneyz" target="_blank" rel="noopener noreferrer" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          Support the project
        </a>
      </footer>
    </div>
  );
}
